import React, { useState } from 'react';
import { SPECIALIZATIONS, SOFTWARE } from '../constants';
import { Project, User } from '../types';
import Input from './ui/Input';
import Select from './ui/Select';
import MultiSelectDropdown from './ui/MultiSelectDropdown';
import { useTranslation } from '../hooks/useTranslation';
import { ContentView } from './ContentViewToggle';

export interface Filters {
  // Common
  name: string;
  
  // User filters
  specialization: string;
  software: string[];
  availableToday: boolean;
  profileType: string;
  registrationStatus: string;
  project: string; // project ID filter
  
  // Project filters
  status: string;
  priority: string;
  projectType: string;
  client: string;
  producerId: string;
  managerId: string;
  
  // Bidding filters
  biddingProject: string; // project ID filter for bidding sheets
}

interface FilterBarProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  projects: Project[];
  users: User[];
  contentView: ContentView;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange, projects, users, contentView }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper function to get translated specialization
  const getSpecializationName = (spec: string) => {
    const key = `specializations.${spec.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;
    const translated = t(key);
    return translated === key ? spec : translated; // fallback to original if not found
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onFilterChange({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, [e.target.name]: e.target.checked });
  };
  
  const handleSoftwareChange = (newSoftware: string[]) => {
    onFilterChange({ ...filters, software: newSoftware });
  };
  
  // Treat both 'users' and 'artists' as artist profiles view
  const isUserView = contentView === 'users' || contentView === 'artists';
  const isProjectView = contentView === 'projects';
  const isBiddingView = contentView === 'bidding';
  const isTeamsView = contentView === 'teams';

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800/95 backdrop-blur-sm shadow-lg border-t border-gray-700 z-20">
      <div className="px-4 md:px-8">
        {/* Mobile toggle button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="md:hidden w-full flex items-center justify-center py-2 text-gray-300 hover:text-white transition-colors"
          aria-label="Toggle filters"
        >
          <svg
            className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        
        {/* Filters - always visible on desktop, collapsible on mobile */}
        <div className={`${isExpanded ? 'block' : 'hidden'} md:block p-4 pt-0 md:pt-4`}>
          {isUserView && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              <Input
                label={t('filters.search')}
                id="name"
                name="name"
                placeholder={t('filters.searchUsers')}
                value={filters.name}
                onChange={handleInputChange}
              />
              <Select
                label={t('filters.specialization')}
                id="specialization"
                name="specialization"
                value={filters.specialization}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allSpecializations')}</option>
                {SPECIALIZATIONS.map(spec => <option key={spec} value={spec}>{getSpecializationName(spec)}</option>)}
              </Select>
              <Select
                label={t('filters.project')}
                id="project"
                name="project"
                value={filters.project}
                onChange={handleInputChange}
              >
                <option value="ALL_PROFILES">{t('filters.allProfiles')}</option>
                {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
              </Select>
              <MultiSelectDropdown
                label={t('filters.software')}
                options={SOFTWARE}
                selectedOptions={filters.software}
                onChange={handleSoftwareChange}
                placeholder={t('filters.all')}
              />
              <div className="flex items-end self-center pb-2">
                <label className="flex items-center space-x-2 text-sm text-gray-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="availableToday"
                    checked={filters.availableToday}
                    onChange={handleCheckboxChange}
                    className="rounded text-indigo-500 focus:ring-indigo-500"
                  />
                  <span>{t('filters.availableToday')}</span>
                </label>
              </div>
              <Select
                label={t('filters.profileType')}
                id="profileType"
                name="profileType"
                value={filters.profileType || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allProfileTypes')}</option>
                <option value="vfx_artist">{t('register.types.vfx_artist')}</option>
                <option value="client">{t('register.types.client')}</option>
                <option value="producer">{t('register.types.producer')}</option>
                <option value="supervisor">{t('register.types.supervisor')}</option>
                <option value="coordinator">{t('register.types.coordinator')}</option>
                <option value="sales">{t('register.types.sales')}</option>
                <option value="finder">{t('register.types.finder')}</option>
                <option value="admin">{t('register.types.admin')}</option>
              </Select>
              <Select
                label={t('filters.registrationStatus')}
                id="registrationStatus"
                name="registrationStatus"
                value={filters.registrationStatus || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allStatuses')}</option>
                <option value="pending">{t('profile.access.pending')}</option>
                <option value="active">{t('profile.access.active')}</option>
                <option value="rejected">{t('profile.access.rejected')}</option>
              </Select>
            </div>
          )}
          
          {isProjectView && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 items-end">
              <Input
                label={t('filters.search')}
                id="name"
                name="name"
                placeholder={t('filters.searchProjects')}
                value={filters.name}
                onChange={handleInputChange}
              />
              <Input
                label={t('filters.client')}
                id="client"
                name="client"
                placeholder={t('filters.clientName')}
                value={filters.client || ''}
                onChange={handleInputChange}
              />
              <Select
                label={t('filters.status')}
                id="status"
                name="status"
                value={filters.status || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allStatuses')}</option>
                <option value="Planning">{t('projectStatus.planning')}</option>
                <option value="In Progress">{t('projectStatus.inProgress')}</option>
                <option value="Completed">{t('projectStatus.completed')}</option>
                <option value="On Hold">{t('projectStatus.onHold')}</option>
                <option value="Cancelled">{t('projectStatus.cancelled')}</option>
              </Select>
              <Select
                label={t('filters.priority')}
                id="priority"
                name="priority"
                value={filters.priority || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allPriorities')}</option>
                <option value="Urgent">{t('priority.urgent') || 'Urgent'}</option>
                <option value="High">{t('priority.high')}</option>
                <option value="Medium">{t('priority.medium')}</option>
                <option value="Low">{t('priority.low')}</option>
              </Select>
              <Select
                label={t('filters.projectType')}
                id="projectType"
                name="projectType"
                value={filters.projectType || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allTypes')}</option>
                <option value="Feature Film">{t('projectTypes.featureFilm')}</option>
                <option value="TV Series">{t('projectTypes.tvSeries')}</option>
                <option value="Commercial">{t('projectTypes.commercial')}</option>
                <option value="Music Video">{t('projectTypes.musicVideo')}</option>
                <option value="Game Cinematic">{t('projectTypes.gameCinematic')}</option>
                <option value="VR/AR">{t('projectTypes.vrAr')}</option>
                <option value="Other">{t('projectTypes.other')}</option>
              </Select>
              <Select
                label={t('filters.producer')}
                id="producerId"
                name="producerId"
                value={filters.producerId || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allProducers')}</option>
                {users
                  .filter(user => user.role === 'Producer' || user.role === 'Super Admin' || user.role === 'Admin' || user.role === 'Coordinator' || user.role === 'Sales')
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))
                }
              </Select>
              <Select
                label={t('filters.manager')}
                id="managerId"
                name="managerId"
                value={filters.managerId || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allManagers')}</option>
                {users
                  .filter(user => user.role === 'Project Manager' || user.role === 'Super Admin' || user.role === 'Admin' || user.role === 'Coordinator')
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))
                }
              </Select>
            </div>
          )}
          
          {isBiddingView && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
              <Input
                label={t('filters.search')}
                id="name"
                name="name"
                placeholder={t('filters.searchBidding')}
                value={filters.name}
                onChange={handleInputChange}
              />
              <Select
                label={t('filters.project')}
                id="biddingProject"
                name="biddingProject"
                value={filters.biddingProject || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allProjects')}</option>
                {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
              </Select>
            </div>
          )}
          
          {isTeamsView && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              <Input
                label={t('filters.search')}
                id="name"
                name="name"
                placeholder={t('filters.searchProjects') || 'Поиск по проектам...'}
                value={filters.name}
                onChange={handleInputChange}
              />
              <Select
                label={t('filters.status')}
                id="status"
                name="status"
                value={filters.status || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allStatuses')}</option>
                <option value="Planning">{t('projectStatus.planning')}</option>
                <option value="In Progress">{t('projectStatus.inProgress')}</option>
                <option value="Completed">{t('projectStatus.completed')}</option>
                <option value="On Hold">{t('projectStatus.onHold')}</option>
                <option value="Cancelled">{t('projectStatus.cancelled')}</option>
              </Select>
              <Select
                label={t('filters.priority')}
                id="priority"
                name="priority"
                value={filters.priority || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allPriorities')}</option>
                <option value="Urgent">{t('priority.urgent') || 'Urgent'}</option>
                <option value="High">{t('priority.high')}</option>
                <option value="Medium">{t('priority.medium')}</option>
                <option value="Low">{t('priority.low')}</option>
              </Select>
              <Select
                label={t('filters.specialization')}
                id="specialization"
                name="specialization"
                value={filters.specialization || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('filters.allSpecializations')}</option>
                {SPECIALIZATIONS.map(spec => (
                  <option key={spec} value={spec}>
                    {getSpecializationName(spec)}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;